import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Global.css';
import './VisualizadorDashboard.css';

function VisualizadorDashboard() {
  // Militar exibe posto/graduação; instrutor civil exibe o vínculo.
  const qualifierOf = (user) =>
    (user?.personnelType === 'civil'
      ? user?.civilBond || 'Civil'
      : user?.militaryRank) || '-';

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('STANDARD'); // STANDARD ou CUSTOM

  const pagination = usePagination(records, 10);
  const currentRecords = pagination.pageItems;

  // Filtros
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    position: '',
    startDateDisplay: '',
    endDateDisplay: '',
  });

  // Converter data de YYYY-MM-DD para DD/MM/YYYY
  const formatDateToBR = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    // Definir intervalo de datas padrão (do dia 16 do mês anterior ao dia 15 do mês atual)
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    let startDate, endDate;

    // Data final: dia 15 do mês atual
    endDate = new Date(currentYear, currentMonth, 15);

    // Data inicial: dia 16 do mês anterior
    startDate = new Date(currentYear, currentMonth - 1, 16);

    // Formatar datas para o formato YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const startDateISO = formatDate(startDate);
    const endDateISO = formatDate(endDate);

    setFilters({
      userId: '',
      startDate: startDateISO,
      endDate: endDateISO,
      position: '',
      startDateDisplay: formatDateToBR(startDateISO),
      endDateDisplay: formatDateToBR(endDateISO),
    });

    // Carregar registros com as datas padrão diretamente
    loadRecordsWithDates(startDateISO, endDateISO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarregar registros automaticamente quando os filtros de data mudarem
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      loadRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.startDate, filters.endDate]);

  const loadRecordsWithDates = async (startDate, endDate) => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await api.get('/time-records', { params });
      setRecords(response.data);
      pagination.setPage(1);
    } catch (error) {
      console.error('Erro ao carregar registros:', error);
      toast.error('Erro ao carregar registros');
    } finally {
      setLoading(false);
    }
  };

  const loadRecords = async () => {
    await loadRecordsWithDates(filters.startDate, filters.endDate);
  };

  // Converter data de DD/MM/YYYY para YYYY-MM-DD
  const formatDateToISO = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return '';
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  // Manipular entrada de data em formato brasileiro
  const handleDateInput = (field, value) => {
    // Remover caracteres não numéricos
    let cleaned = value.replace(/\D/g, '');

    // Formatar como dd/mm/yyyy
    let formatted = cleaned;
    if (cleaned.length >= 2) formatted = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
    if (cleaned.length >= 5) formatted = cleaned.slice(0, 5) + '/' + cleaned.slice(5, 9);

    // Atualizar o valor formatado
    setFilters({ ...filters, [field + 'Display']: formatted });

    // Se a data estiver completa (10 caracteres com barras), converter para ISO e salvar
    if (formatted.length === 10) {
      const iso = formatDateToISO(formatted);
      if (iso) {
        setFilters({ ...filters, [field]: iso, [field + 'Display']: formatted });
      }
    }
  };

  // Manipular seleção de data via calendário
  const handleDatePicker = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
      [field + 'Display']: formatDateToBR(value)
    });
  };

  const formatDecimalToHours = (decimal) => {
    if (!decimal) return '0h 00min';
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
  };

  const exportToQTM = async () => {
    try {
      const payload = {
        type: reportType,
        format: 'PDF',
      };

      // Se for personalizado, adicionar período
      if (reportType === 'CUSTOM') {
        if (!filters.startDate || !filters.endDate) {
          toast.error('Para relatórios personalizados, informe o período');
          return;
        }
        payload.periodStart = filters.startDate;
        payload.periodEnd = filters.endDate;
      }

      // Gerar o relatório
      const response = await api.post('/reports/generate', payload);
      toast.success('QTM gerado com sucesso!');

      // Fazer download imediatamente
      if (response.data.id) {
        await downloadReport(response.data.id);
      }
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast.error('Erro ao exportar QTM: ' + (error.response?.data?.message || error.message));
    }
  };

  const downloadReport = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}/download`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      let fileName = `qtm_${dateStr}.pdf`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success('Download iniciado!');
    } catch (error) {
      console.error('Erro ao baixar relatório:', error);
      toast.error('Erro ao baixar relatório: ' + (error.response?.data?.message || error.message));
    }
  };

  // Estatísticas
  const totalHours = records.reduce((sum, record) => sum + (record.totalHours || 0), 0);
  const pendingRecords = records.filter(record => !record.exitTime).length;
  const completedRecords = records.filter(record => record.exitTime).length;

  return (
    <>
      <Navbar />
      <div className="visualizador-dashboard">
        <div className="visualizador-header">
          <h1>Relatórios e Histórico</h1>
          <p>Visualização e exportação de registros de ponto</p>
        </div>

      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-value">{records.length}</div>
          <div className="stat-label">Total de Registros</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{completedRecords}</div>
          <div className="stat-label">Completos</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{pendingRecords}</div>
          <div className="stat-label">Pendentes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{formatDecimalToHours(totalHours)}</div>
          <div className="stat-label">Total de Horas</div>
        </div>
      </div>

      <div className="filters-section">
        <h2>Filtros</h2>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Tipo de Relatório</label>
            <select
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                border: '2px solid #e2e8f0',
                borderRadius: '8px'
              }}
            >
              <option value="STANDARD">Padrão (16 mês anterior → 15 mês atual)</option>
              <option value="CUSTOM">Personalizado</option>
            </select>
          </div>

          {reportType === 'CUSTOM' && (
            <>
              <div className="filter-group" style={{ position: 'relative' }}>
                <label>Data Inicial:</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={filters.startDateDisplay}
                onChange={(e) => handleDateInput('startDate', e.target.value)}
                placeholder="dd/mm/aaaa"
                maxLength="10"
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  fontSize: '14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleDatePicker('startDate', e.target.value)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '24px',
                  height: '24px',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '18px',
                color: '#6b7280',
                pointerEvents: 'none'
              }}>📅</span>
            </div>
          </div>

          <div className="filter-group" style={{ position: 'relative' }}>
            <label>Data Final:</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={filters.endDateDisplay}
                onChange={(e) => handleDateInput('endDate', e.target.value)}
                placeholder="dd/mm/aaaa"
                maxLength="10"
                style={{
                  width: '100%',
                  padding: '10px 40px 10px 12px',
                  fontSize: '14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleDatePicker('endDate', e.target.value)}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '24px',
                  height: '24px',
                  opacity: 0,
                  cursor: 'pointer'
                }}
              />
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '18px',
                color: '#6b7280',
                pointerEvents: 'none'
              }}>📅</span>
            </div>
          </div>
            </>
          )}
        </div>

        <div className="filter-actions">
          <button className="btn-export" onClick={exportToQTM} disabled={records.length === 0}>
            Exportar QTM (PDF)
          </button>
        </div>
      </div>

      <div className="records-section">
        <h2>Registros ({records.length})</h2>
        {loading ? (
          <p className="loading">Carregando...</p>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Nome</th>
                    <th>Posto/Vínculo</th>
                    <th>Função</th>
                    <th>Entrada</th>
                    <th>Saída</th>
                    <th>Total Horas</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {currentRecords.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center empty-state">
                        <strong>Nenhum registro encontrado</strong>
                        Ajuste o período ou os filtros e consulte de novo.
                      </td>
                    </tr>
                  ) : (
                    currentRecords.map((record) => (
                      <tr
                        key={record.id}
                        className={!record.exitTime ? 'row-pending' : ''}
                      >
                        <td className="col-time">{record.date.split('-').reverse().join('/')}</td>
                        <td className="cell-primary">{record.user?.name}</td>
                        <td>{qualifierOf(record.user)}</td>
                        <td>{record.position?.name || record.user?.positions?.map(p => p.name).join(', ') || record.user?.position || '-'}</td>
                        <td className="col-time">{record.entryTime?.substring(0, 5) || '-'}</td>
                        <td className="col-time">
                          {record.exitTime ? (
                            record.exitTime.substring(0, 5)
                          ) : (
                            <span className="pending-badge">Pendente</span>
                          )}
                        </td>
                        <td className="col-time">
                          {!record.exitTime ? (
                            <span className="not-counted">Não contabilizado</span>
                          ) : (
                            <strong>{formatDecimalToHours(record.totalHours)}</strong>
                          )}
                        </td>
                        <td className="cell-muted">{record.observation || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              id="visualizador"
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              from={pagination.from}
              to={pagination.to}
              pageSize={pagination.pageSize}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
              itemLabel="registros"
            />
          </>
        )}
      </div>
      </div>
    </>
  );
}

export default VisualizadorDashboard;
